/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement } from 'react';
import type { ShopperProducts } from '@/scapi';
import ImageGallery from '@/components/image-gallery';
import ProductInfo from './product-info';
import ProductCartActions from '@/components/product-cart-actions';
import ProductViewProvider from '@/providers/product-view';
import { Region } from '@/components/region';
import ImpactCalloutSlot from '@/components/impact-callout-slot';
import ProductTabs from './product-tabs';
import type { PageWithComponentData } from '@/lib/page-designer/page-loader.server';
import { useProductImages } from '@/hooks/product/use-product-images';
import { useSelectedVariations } from '@/hooks/product/use-selected-variations';
import { isProductSet, isProductBundle } from '@/lib/product/product-utils';
import { UITarget } from '@/targets/ui-target';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import type { AppConfig } from '@/types/config';
import { ProductBadge } from '@/components/product-badges/product-badges';
import { productBadgeColorClasses } from '@/components/product-badges/product-badge-variants';
import { productHasBadge } from '@/lib/product/product-badges';

interface ProductViewProps {
    product: ShopperProducts.schemas['Product'];
    mode?: 'add' | 'edit';
    /**
     * Page Designer page carrying the `impactContent` region (the mission-reminder callout below
     * the Add to Cart block). Omit outside the PDP route — the region simply falls back to the
     * built-in `ImpactCalloutSlot` copy.
     */
    page?: Promise<PageWithComponentData | null> | PageWithComponentData | null;
}

/**
 * ProductView component renders a complete product detail view with image gallery and product information.
 *
 * @param props - The component props
 * @param props.product - The product data from Salesforce Commerce Cloud containing all product details,
 *                        variants, pricing, and metadata
 *
 * @returns A React element containing the complete product view layout
 *
 * @example
 * ```tsx
 * <ProductView product={productData} />
 * ```
 */
export default function ProductView({ product, page = null }: ProductViewProps): ReactElement {
    // Calculate directly without useMemo since these are simple operations
    const isProductASet = isProductSet(product);
    const isProductABundle = isProductBundle(product);

    // Get selected attributes from URL parameters for image gallery
    const selectedAttributes = useSelectedVariations({ product });
    const { galleryImages } = useProductImages({
        product,
        selectedAttributes,
    });

    // "NEW" badge on the main gallery image, driven by the same badge config used on PLP tiles
    // (config.global.badges), read directly off the full product detail shape's custom attribute.
    const config = useConfig<AppConfig>();
    const newBadgeConfig = config.global.badges?.find((badge) => badge.value?.toLowerCase() === 'new');
    const isNewProduct = Boolean(
        newBadgeConfig && productHasBadge(product as unknown as Record<string, unknown>, newBadgeConfig)
    );
    const newBadge =
        isNewProduct && newBadgeConfig ? (
            <ProductBadge className={productBadgeColorClasses[newBadgeConfig.color]}>
                {newBadgeConfig.label}
            </ProductBadge>
        ) : undefined;

    return (
        <ProductViewProvider product={product} mode="add">
            {/*
                Mobile: single column, `order-*` stacks gallery -> info (header/price/size row with Size
                Chart/quantity + Add to Cart/impact callout).
                Desktop (`lg+`): gallery and info sit side by side (auto-placed into row 1, columns 1/2).
                The Details/Size Chart tabs render below the grid so they span the full container width
                rather than the gallery column's width.
            */}
            <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-4 lg:gap-x-12 lg:gap-y-6">
                {/* Gallery */}
                <div className="order-1 lg:order-none">
                    <ImageGallery
                        key={product.id}
                        images={galleryImages}
                        eager={!isProductASet && !isProductABundle}
                        showNavigationArrows
                        navigationArrowSize="lg"
                        verticalThumbnails
                        enableZoom
                        badge={newBadge}
                        productName={product.name}
                    />
                    <UITarget targetId="sfcc.pdp.agent.productHelper" />
                </div>

                {/* Info: header, price, variants, actions, impact callout */}
                <div className="order-2 lg:order-none">
                    {/* Quantity stepper and Add to Cart share one row; sets/bundles have no quantity
                        row, so their cart actions keep rendering on their own below the info block. */}
                    {isProductASet || isProductABundle ? (
                        <>
                            <ProductInfo product={product} />
                            <ProductCartActions product={product} />
                        </>
                    ) : (
                        <ProductInfo
                            product={product}
                            quantityAction={<ProductCartActions product={product} className="mt-0" />}
                        />
                    )}
                    {/* @sfdc-extension-block-start SFDC_EXT_SHIPPING_DELIVERY */}
                    {/* @sfdc-extension-block-end SFDC_EXT_SHIPPING_DELIVERY */}
                    {/* Merchant-authorable mission callout. `errorElement` doubles as the empty-region
                        fallback on the live storefront, so the built-in copy shows until a merchant
                        drops a component into the region. */}
                    <Region
                        className="mt-6"
                        page={page}
                        regionId="impactContent"
                        errorElement={
                            <div className="mt-6">
                                <ImpactCalloutSlot />
                            </div>
                        }
                    />
                    <UITarget targetId="sfcc.pdp.faq" />
                </div>
            </div>

            {/* Details / Size Chart tabs — full container width, below the split-column fold */}
            <ProductTabs product={product} className="mt-12" />
        </ProductViewProvider>
    );
}
